import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search,
  Filter,
  Edit, 
  Trash2,
  Plus,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { 
  getEmployeeActivityTypes, 
  deleteEmployeeActivityType,
  ActivityType 
} from '../services/activityApi';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { ConfirmModal } from '../components/ConfirmModal';
import { useVisibleSelection } from '../hooks/useVisibleSelection';

export function EmployeeActivityTypesListPage() {
  const navigate = useNavigate();
  
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filteredTypes, setFilteredTypes] = useState<ActivityType[]>([]);
  const visibleTypeIds = useMemo(() => filteredTypes.map((t) => t.id), [filteredTypes]);
  const {
    selectedIds: selectedTypeIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleTypeIds);

  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchActivityTypes();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredTypes(activityTypes);
    } else {
      const searchLower = search.toLowerCase();
      setFilteredTypes(
        activityTypes.filter(type => 
          type.name.toLowerCase().includes(searchLower) ||
          type.description?.toLowerCase().includes(searchLower)
        )
      );
    }
  }, [search, activityTypes]);

  const fetchActivityTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const types = await getEmployeeActivityTypes();
      setActivityTypes(types);
    } catch (error: any) {
      console.error('Fetch employee activity types error:', error);
      setError(error.message || 'Failed to load employee activity types');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (typeId: string) => {
    navigate(`/planning/employee-activity-types/${typeId}/edit`);
  };

  const handleCreate = () => {
    navigate('/planning/employee-activity-types/create');
  };

  const handleDelete = async (typeId: string, typeName: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le type d'activité employé "${typeName}" ?`)) {
      return;
    }

    try {
      await deleteEmployeeActivityType(typeId);
      await fetchActivityTypes();
    } catch (error: any) {
      alert(error.message || 'Failed to delete employee activity type');
    }
  };

  const openBulkDeleteConfirm = () => {
    if (selectedTypeIds.size === 0) return;
    setBulkDeleteConfirm({ isOpen: true, ids: Array.from(selectedTypeIds) });
  };
  const cancelBulkDelete = () => setBulkDeleteConfirm({ isOpen: false, ids: [] });
  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.ids.length === 0) return;
    try {
      setBulkDeleting(true);
      const failed: string[] = [];
      for (const id of bulkDeleteConfirm.ids) {
        try {
          await deleteEmployeeActivityType(id);
        } catch {
          failed.push(id);
        }
      }
      setBulkDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await fetchActivityTypes();
      if (failed.length > 0) {
        alert(`${failed.length} type(s) n’ont pas pu être supprimés.`);
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const getVisibilityBadge = (visibility: string) => {
    const colorMap: Record<string, string> = {
      'INTERNAL': 'bg-blue-100 text-blue-800',
      'FRONTOFFICE': 'bg-green-100 text-green-800',
      'PRIVATE': 'bg-gray-100 text-gray-800'
    };

    const labelMap: Record<string, string> = {
      'INTERNAL': 'Interne',
      'FRONTOFFICE': 'Public',
      'PRIVATE': 'Privé'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        colorMap[visibility] || 'bg-gray-100 text-gray-800'
      }`}>
        {labelMap[visibility] || visibility}
      </span>
    );
  };

  if (loading && activityTypes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">Chargement des types d'activités employé...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="dt-list-toolbar flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Types d'Activités Employé</span>
            </h1>
            <p className="text-textSecondary mt-1">
              {filteredTypes.length} type{filteredTypes.length !== 1 ? 's' : ''} d'activité employé
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un Type Employé</span>
          </button>
          {selectedCount > 0 && (
            <button
              onClick={openBulkDeleteConfirm}
              disabled={bulkDeleting}
              className="ml-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
              title="Supprimer les types sélectionnés"
            >
              <Trash2 className="w-4 h-4" />
              <span>Supprimer ({selectedCount})</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Rechercher un type d'activité employé..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-field"
            />
          </div>
          
          {/* Clear Filters */}
          <button
            onClick={() => setSearch('')}
            className="btn-secondary flex items-center justify-center space-x-2"
          >
            <Filter className="w-4 h-4" />
            <span>Réinitialiser</span>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visibilité
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Blocage
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTypes.map((type) => (
                <tr key={type.id} className="group hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedTypeIds.has(type.id)}
                      onChange={() => toggleSelect(type.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: type.defaultColor }} />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{type.name}</div>
                        {type.description && (
                          <div className="text-sm text-gray-500">{type.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {getVisibilityBadge(type.defaultVisibility)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      type.isBlocking ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {type.isBlocking ? 'Bloquant' : 'Non bloquant'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(type.id)}
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(type.id, type.name)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>
      </div>

      {bulkDeleteConfirm.isOpen && (
        <ConfirmModal
          isOpen={bulkDeleteConfirm.isOpen}
          title="Supprimer les types d'activités employé ?"
          message={`Êtes-vous sûr de vouloir supprimer ${bulkDeleteConfirm.ids.length} type(s) d'activités employé ?`}
          confirmLabel={bulkDeleting ? 'Suppression...' : 'Supprimer'}
          cancelLabel="Annuler"
          onConfirm={confirmBulkDelete}
          onClose={cancelBulkDelete}
          isLoading={bulkDeleting}
        />
      )}
    </div>
  );
}
